import React, { useEffect, useRef, useState } from "react"
import * as am5 from "@amcharts/amcharts5"
import * as am5radar from "@amcharts/amcharts5/radar"
import * as am5xy from "@amcharts/amcharts5/xy"
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated"
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting"

import { formatNumberDisplayed, getNumberFormat, transformeData } from "../utils/global"
import {
  createLegend,
  clearLegends,
  createExportMenu,
  configureNumberFormatting,
} from "../utils/utils"

// Types
interface LabelConfig {
  labelValue?: number
  customLabelText?: string
  fontSizeLabel?: number
  colorLabel?: string
  tickWidth?: number
  tickColor?: string
  showLine?: boolean
}

interface GaugeChartProps {
  idChart?: string
  width?: string
  height?: string
  dataChart?: any[]
  valueData?: any
  dataNames?: string[]
  defaultColor?: string
  colorSetTheme?: string[]
  showClockHand?: boolean
  enableExportMenu?: boolean
  // SPECS
  axisStrictMinMax?: boolean
  showOnlyMinMaxLabel?: boolean
  showAxisLabel?: boolean
  labels?: LabelConfig[]
  showGrid?: boolean
  innerRadius?: number
  startAngle?: number
  endAngle?: number
  // AXIS
  axisMin?: number
  axisMax?: number
  axisStrokeColor?: string
  axisStrokeWidth?: number
  axisStrokeOpacity?: number
  axisStrokeLinecap?: string
  minGridDistance?: number
  valueForceMinMax?: boolean
  valueAxisMin?: number
  valueAxisMax?: number
  // RANGES
  isGradient?: boolean
  gradientColors?: string[]
  isGradientRange?: boolean
  ranges?: any[]
  widgetTitle?: string
  // LEGEND
  visibilityLegend?: boolean
  legendPosition?: "top" | "bottom" | "left" | "right"
  displayTypeLegendLabels?: "truncate" | "wrap" | "default"
  externalLegendContainer?: boolean
  showHandTooltip?: boolean
  enableRangeTooltips?: boolean
  // Title
  showCenteredValue?: boolean
  fontWeightTitleCenter?: string
  fontSizeTitleCenter?: number
  colorTitleCenter?: string
  customCenteredValue?: string
  // formats
  numberDecimal?: number
  decimalSeparator?: string
  showSpaceForThousand?: boolean
  // autres
  requestLoad?: any
  fieldsData?: any
}

const GaugeChart: React.FC<GaugeChartProps> = ({
  idChart,
  width = "100%",
  height = "100%",
  dataChart = [],
  valueData = [],
  dataNames = [],
  defaultColor = "#e67e22",
  colorSetTheme = ["#00b9c9", "#0b8a93", "#3b595b"],
  showClockHand = true,
  enableExportMenu = false,
  axisStrictMinMax = true,
  showOnlyMinMaxLabel = true,
  showAxisLabel = true,
  labels = [],
  showGrid = false,
  innerRadius = 50,
  startAngle = -180,
  endAngle = 0,
  axisMin = 0,
  axisMax = 100,
  axisStrokeColor = "#000000",
  axisStrokeWidth = 0,
  axisStrokeOpacity = 0,
  axisStrokeLinecap = "round",
  minGridDistance = 100,
  valueForceMinMax,
  valueAxisMin,
  valueAxisMax,
  isGradient = false,
  gradientColors = [],
  isGradientRange = false,
  ranges = [],
  widgetTitle = "",
  visibilityLegend = false,
  legendPosition,
  displayTypeLegendLabels,
  externalLegendContainer = false,
  showHandTooltip = true,
  enableRangeTooltips = true,
  showCenteredValue = false,
  fontWeightTitleCenter = "500",
  fontSizeTitleCenter = 20,
  colorTitleCenter = "#000",
  customCenteredValue = "",
  numberDecimal,
  decimalSeparator,
  showSpaceForThousand = true,
  requestLoad,
  fieldsData,
}) => {
  const ID = useRef(idChart || `GaugeChart${Math.random()}`).current
  const chartRef = useRef<am5radar.RadarChart | null>(null)
  const rootRef = useRef<am5.Root | null>(null)
  const [idLegendState] = useState(`${ID}-legend`)
  const legendRootRef = useRef<am5.Root | null>(null)

  const dataChartUniqValue = Object.values(
    dataChart.reduce((acc: any, { id, value, name }: any) => {
      if (!acc[id]) acc[id] = { id, name, value: 0 }
      acc[id].value += value
      return acc
    }, {})
  )

  const dataTransformed = transformeData(requestLoad, fieldsData, dataChartUniqValue)

  const getSumFromData = (data: any[]): number => {
    let sum = 0
    if (!data || data.length === 0) return 0

    // Handle data with { value } format
    if (data[0]?.value !== undefined) {
      data.forEach(dataRow => (sum += dataRow.value))
      return sum
    }

    // Handle data with { startValue, endValue } format (ranges)
    if (data[0]?.endValue !== undefined) {
      // Return the max endValue
      return Math.max(...data.map(d => d.endValue || 0))
    }

    return 0
  }

  const initChart = () => {
    // Guard against duplicate initialization (StrictMode)
    if (rootRef.current) {
      return
    }

    const root = am5.Root.new(ID)
    root._logo?.dispose()
    rootRef.current = root

    root.setThemes([am5themes_Animated.new(root)])

    const chart = root.container.children.push(
      am5radar.RadarChart.new(root, {
        panX: false,
        panY: false,
        startAngle: startAngle,
        endAngle: endAngle,
        innerRadius: am5.percent(innerRadius),
      })
    )

    chartRef.current = chart

    setupChart(chart, root)
    createAxis(chart, root)
  }

  const setupChart = (chart: am5radar.RadarChart, root: am5.Root) => {
    // Number formatting
    configureNumberFormatting(root, {
      decimalSeparator,
      numberDecimal,
      showSpaceForThousand,
    })

    // Export menu
    createExportMenu(root, {
      enableExportMenu: enableExportMenu ?? false,
      filePrefix: widgetTitle,
      dataSource: dataTransformed,
    })

    // Legend
    if (visibilityLegend) {
      if (externalLegendContainer) {
        // Create external legend in separate container
        if (legendRootRef.current) {
          legendRootRef.current.dispose()
        }
        const legendRoot = am5.Root.new(idLegendState)
        legendRootRef.current = legendRoot

        const legend = am5.Legend.new(legendRoot, {
          width: am5.percent(100),
          layout: legendRoot.horizontalLayout,
        })

        legendRoot.container.children.push(legend)

        const legendData = dataChart.map((data, index) => {
          const color = index < colorSetTheme.length ? colorSetTheme[index] : defaultColor
          return { name: data.name, fill: am5.color(color) }
        })
        legend.data.setAll(legendData)
      } else {
        // Internal legend - use shared utility
        // Use root.container for legend to avoid messing up RadarChart layout
        const legend = createLegend(root.container, root, {
          visibilityLegend: true,
          legendPosition,
          displayTypeLegendLabels,
        })

        if (legend) {
          const legendData = dataChart.map((data, index) => {
            const color = index < colorSetTheme.length ? colorSetTheme[index] : defaultColor
            return { name: data.name, fill: am5.color(color) }
          })
          legend.data.setAll(legendData)
        }
      }
    }

    // Centered title
    if (showCenteredValue && customCenteredValue !== "") {
      const title = chart.radarContainer.children.push(
        am5.Label.new(root, {
          text: customCenteredValue,
          fontSize: fontSizeTitleCenter,
          fontWeight: fontWeightTitleCenter as any,
          fill: am5.color(colorTitleCenter),
          centerX: am5.p50,
          centerY: am5.p100,
          textAlign: "center",
        })
      )
    }
  }

  const createAxis = (chart: am5radar.RadarChart, root: am5.Root) => {
    const toNum = (v: any): number | undefined => {
      const n = Number(v)
      return Number.isFinite(n) ? n : undefined
    }

    const total = getSumFromData(dataChart)

    const forcedMin = valueForceMinMax ? toNum(valueAxisMin) : undefined
    const forcedMax = valueForceMinMax ? toNum(valueAxisMax) : undefined

    // When valueForceMinMax is true, use forced values; otherwise use data total
    const axisMinCalculated = forcedMin ?? 0
    const axisMaxCalculated = forcedMax ?? (total > 0 ? total : 100)

    const dataNamesToUse = dataChart.map(row => row.name)
    let rangeToDisplay: any[] = []

    let accumulator = 0
    rangeToDisplay = dataChart.map(row => {
      const firstValue = accumulator
      const secondValue = firstValue + Number(row.value || 0)
      accumulator = secondValue
      return { startValue: firstValue, endValue: secondValue }
    })

    if (
      rangeToDisplay.length === 0 &&
      ranges &&
      ranges.length &&
      Object.prototype.hasOwnProperty.call(ranges[0], "startValue")
    ) {
      rangeToDisplay = ranges
    }

    // Axis
    const axis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        min: axisMinCalculated,
        max: axisMaxCalculated,
        strictMinMax: axisStrictMinMax,
        renderer: am5radar.AxisRendererCircular.new(root, {
          strokeWidth: axisStrokeWidth,
          strokeOpacity: axisStrokeOpacity,
          stroke: am5.color(axisStrokeColor),
          minGridDistance: showGrid ? minGridDistance : undefined,
        }),
      })
    )

    axis.get("renderer").grid.template.set("visible", showGrid)
    axis.get("renderer").labels.template.set("visible", showAxisLabel)

    if (showOnlyMinMaxLabel) {
      axis.get("renderer").labels.template.set("visible", false)

      const rangeMin = axis.createAxisRange(
        axis.makeDataItem({
          value: axisMinCalculated,
        })
      )
      rangeMin.get("label")?.set("text", String(axisMinCalculated))

      const rangeMax = axis.createAxisRange(
        axis.makeDataItem({
          value: axisMaxCalculated,
        })
      )
      rangeMax.get("label")?.set("text", String(axisMaxCalculated))
    }

    // Gradient or ranges
    if (isGradient && gradientColors && gradientColors.length) {
      const gradient = am5.LinearGradient.new(root, {
        stops: gradientColors.map((color, i) => ({
          color: am5.color(color),
          offset: i / (gradientColors.length - 1),
        })),
      })

      const gradientRange = axis.createAxisRange(
        axis.makeDataItem({
          value: axisMinCalculated,
          endValue: axisMaxCalculated,
        })
      )

      gradientRange.get("axisFill")?.setAll({
        fill: gradient as any,
        fillOpacity: 1,
        visible: true,
      })
    } else {
      const rangeToDisplayToUse = isGradientRange ? ranges : rangeToDisplay
      rangeToDisplayToUse?.forEach((range, index) => {
        const nameToUse = index < dataNamesToUse.length ? dataNamesToUse[index] : "Undefined Name"

        const axisRange = axis.createAxisRange(
          axis.makeDataItem({
            value: range.startValue,
            endValue: range.endValue,
          })
        )

        const fillColor = range.fillColor
          ? am5.color(range.fillColor)
          : index < colorSetTheme.length
          ? am5.color(colorSetTheme[index])
          : am5.color(defaultColor)

        axisRange.get("axisFill")?.setAll({
          fill: fillColor,
          fillOpacity: range?.fillOpacity ?? 1,
          visible: true,
        })

        if (showAxisLabel && index !== 0) {
          axisRange.get("label")?.set("text", range.startValue.toFixed(numberDecimal || 0))
        }

        if (enableRangeTooltips) {
          const startValueFormatted = formatNumberDisplayed(
            range.startValue,
            numberDecimal,
            showSpaceForThousand,
            decimalSeparator
          )
          const endValueFormatted = formatNumberDisplayed(
            range.endValue,
            numberDecimal,
            showSpaceForThousand,
            decimalSeparator
          )

          axisRange.get("axisFill")?.setAll({
            tooltipText: `[bold]${nameToUse}[/]: ${startValueFormatted} - ${endValueFormatted}`,
            interactive: true,
          })
        } else {
          axisRange.get("axisFill")?.set("interactive", false)
        }
      })
    }

    // Clock hand
    if (showClockHand) {
      const axisDataItem = axis.makeDataItem({
        value: 0,
      })

      const hand = am5radar.ClockHand.new(root, {
        pinRadius: showCenteredValue && customCenteredValue !== "" ? 0 : 10,
        radius: am5.percent(90),
        bottomWidth: 15,
        topWidth: 0,
      })

      const pin = hand.pin
      if (pin) {
        pin.setAll({
          fill: am5.color("#000000"),
          stroke: am5.color("#000000"),
        })
      }

      const handGraphics = hand.hand
      if (handGraphics) {
        handGraphics.setAll({
          fill: am5.color("#000000"),
          stroke: am5.color("#000000"),
        })
      }

      axisDataItem.set(
        "bullet",
        am5xy.AxisBullet.new(root, {
          sprite: hand,
        })
      )

      axis.createAxisRange(axisDataItem)
      axisDataItem.get("grid")?.set("visible", false)
      axisDataItem.get("tick")?.set("visible", false)

      const computeValue = (): number => {
        const values = dataChart
          .map(row => {
            const v = Object.values(row)[0]
            return Number(v)
          })
          .filter(v => Number.isFinite(v))
        const avg = values.length > 0 ? values.reduce((sum, v) => sum + v, 0) / values.length : 0
        const final = typeof valueData === "number" ? valueData : avg
        return Math.max(axisMinCalculated, Math.min(axisMaxCalculated, final))
      }

      axisDataItem.set("value", computeValue())

      if (showHandTooltip) {
        const handTooltip = am5.Tooltip.new(root, {
          getFillFromSprite: false,
          autoTextColor: false,
          pointerOrientation: "down",
        })

        handTooltip.get("background")?.setAll({
          fill: am5.color("#000"),
          strokeOpacity: 0,
        })

        handTooltip.label.setAll({
          fill: am5.color("#fff"),
        })

        hand.set("tooltip", handTooltip)

        const updateTooltip = () => {
          const v = computeValue()
          const txt = formatNumberDisplayed(
            v,
            numberDecimal,
            showSpaceForThousand,
            decimalSeparator
          )
          handTooltip.label.set("text", txt)
        }

        // There is no direct "value" event on hand when used this way,
        // we update the tooltip when value changes or initially.
        updateTooltip()
      }
    }

    // Additional labels
    labels?.forEach(label => {
      if (label?.labelValue !== undefined) {
        const axisRange = axis.createAxisRange(
          axis.makeDataItem({
            value: label.labelValue,
          })
        )

        const customLabelText = label?.customLabelText
          ? label.customLabelText
          : label.labelValue.toFixed(numberDecimal || 0)

        axisRange.get("label")?.setAll({
          text: customLabelText,
          fontSize: label?.fontSizeLabel,
          fill: label?.colorLabel ? am5.color(label.colorLabel) : undefined,
        })

        axisRange.get("grid")?.setAll({
          strokeWidth: label?.tickWidth,
          stroke: label?.tickColor ? am5.color(label.tickColor) : undefined,
          strokeOpacity: label?.showLine ? 1 : 0,
        })
      }
    })
  }

  useEffect(() => {
    initChart()

    return () => {
      if (rootRef.current) {
        rootRef.current.dispose()
      }
      if (legendRootRef.current) {
        legendRootRef.current.dispose()
      }
    }
  }, [])

  // Effect for updates - runs on every render (like old amCharts 4 code)
  useEffect(() => {
    if (chartRef.current && rootRef.current) {
      const chart = chartRef.current
      const root = rootRef.current

      // Clear legends
      clearLegends(root.container)

      // Update chart properties
      chart.set("startAngle", startAngle)
      chart.set("endAngle", endAngle)
      chart.set("innerRadius", am5.percent(innerRadius))

      // Clear existing axis
      chart.xAxes.clear()

      // Reapply chart configurations
      setupChart(chart, root)

      // Recreate axis with current data
      createAxis(chart, root)
    }
  })

  return (
    <>
      <div id={ID} style={{ width, height }} />
      {visibilityLegend && externalLegendContainer && (
        <div id={idLegendState} style={{ width: "100%", height: "auto" }} />
      )}
    </>
  )
}

export default GaugeChart
